import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ChipSelectOption } from "@/types";

const UNSELECTED_CLASS =
  "border border-dashed border-lever-border-light bg-lever-surface-default-on-elevation/60 text-lever-text-label hover:border-lever-border-action hover:bg-lever-surface-neutral hover:text-lever-text-label";

export const DropDownCell = <T extends string>({
  options,
  placeholder = "Select",
  value,
  onChange,
  clearable = true,
  iconOnly = false,
  isSmall = false,
  pill = false,
  readOnly = false,
}: {
  options: ChipSelectOption<T>[];
  placeholder?: string;
  value: T | null;
  onChange: (value: T | null) => void;
  clearable?: boolean;
  iconOnly?: boolean;
  isSmall?: boolean;
  pill?: boolean;
  readOnly?: boolean;
}) => {
  const selectedOption = options.find((option) => option.value === value);
  const SelectedIcon = selectedOption?.icon;
  // iconOnly selected → small ghost icon; everything else → neutral circle trigger
  const isIconOnlySelected = iconOnly && selectedOption;

  const trigger = pill ? (
    <Button
      type="button"
      variant="ghost"
      disabled={readOnly}
      aria-label={selectedOption?.label ?? placeholder}
      className={cn(
        "h-6 shrink-0 gap-1 rounded-full border px-2.5 text-xs font-medium disabled:opacity-100",
        isSmall && "h-[18px] gap-0.5 px-1.5 text-[10px]",
        selectedOption
          ? selectedOption.selectedClassName
          : cn(UNSELECTED_CLASS, "hover:text-lever-text-label"),
      )}
    >
      {SelectedIcon ? (
        <SelectedIcon
          className={cn(
            "h-3 w-3 shrink-0",
            isSmall && "h-2.5 w-2.5",
            selectedOption.iconClassName ?? "text-lever-text-label",
          )}
          strokeWidth={2}
          aria-hidden
        />
      ) : null}
      {selectedOption?.label ?? placeholder}
    </Button>
  ) : (
    <Button
      type="button"
      variant={isIconOnlySelected ? "ghost" : "outlineSecondary"}
      size={isIconOnlySelected ? undefined : "sm"}
      disabled={readOnly}
      aria-label={selectedOption?.label ?? placeholder}
      className={cn(
        isIconOnlySelected
          ? cn(
              "h-6 w-6 shrink-0 rounded-full p-0",
              isSmall && "h-5 w-5",
              selectedOption.selectedClassName,
            )
          : cn(
              "h-8 w-8 shrink-0 rounded-full p-0",
              iconOnly && "h-6 w-6",
              isSmall && "h-5 w-5",
              selectedOption
                ? selectedOption.selectedClassName
                : UNSELECTED_CLASS,
            ),
      )}
    >
      {SelectedIcon ? (
        <SelectedIcon
          className={cn(
            "h-4 w-4 shrink-0",
            isSmall && "h-3 w-3",
            iconOnly &&
              (selectedOption.iconClassName ?? "text-lever-text-footnote"),
          )}
          strokeWidth={2}
          aria-hidden
        />
      ) : (
        <PlusIcon
          className={cn(
            "h-3.5 w-3.5 shrink-0 opacity-70",
            isSmall && "h-3 w-3",
          )}
          strokeWidth={2}
          aria-hidden
        />
      )}
    </Button>
  );

  if (readOnly) return trigger;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[148px]">
        {clearable && value ? (
          <>
            <DropdownMenuItem
              className="text-lever-text-label"
              onSelect={() => {
                onChange(null);
              }}
            >
              Clear
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {options.map((option) => {
          const OptionIcon = option.icon;

          return (
            <DropdownMenuItem
              key={option.value}
              className={cn(
                "gap-2",
                value === option.value &&
                  "bg-lever-surface-default-on-elevation font-medium",
              )}
              onSelect={() => {
                onChange(option.value);
              }}
            >
              {OptionIcon ? (
                <OptionIcon
                  className="h-4 w-4 shrink-0 text-lever-text-footnote"
                  strokeWidth={2}
                  aria-hidden
                />
              ) : null}
              {option.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
